import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useEntryBySession } from '../hooks/useUserEntries';
import { useRaffle } from '../hooks/useRaffle';
import { Button, Spinner } from '../components/ui';
import ConfettiExplosion from '../components/shared/ConfettiExplosion';
import { formatCurrency } from '../lib/utils';

/**
 * EntrySuccess Page - Lucky Number Design System
 *
 * Shown after successful Stripe checkout.
 * Displays ticket numbers with celebration animation.
 */
export default function EntrySuccess() {
  const { id: raffleId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const { entry, loading, error, notFound, ticketNumbers, ticketCount } = useEntryBySession(sessionId, raffleId);
  const { raffle, loading: raffleLoading } = useRaffle(raffleId);

  // Loading state
  if (loading || raffleLoading) {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" className="mx-auto mb-4" />
          <p className="text-cream/70">Confirming your entry...</p>
        </div>
      </div>
    );
  }

  // Entry not found - likely webhook not configured in dev
  if (notFound) {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-display text-cream mb-2">Payment Received!</h1>
          <p className="text-cream/60 mb-4">
            Your payment was successful. Your entry is being processed.
          </p>
          <p className="text-cream/40 text-sm mb-6">
            If you're in development mode, make sure the Stripe webhook is configured:
            <code className="block mt-2 bg-midnight-light p-2 rounded text-xs text-left">
              stripe listen --forward-to localhost:5001/demo-raflr/europe-west1/stripeWebhook
            </code>
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={`/competition/${raffleId}`}>
              <Button variant="secondary">Back to Competition</Button>
            </Link>
            <Link to="/browse">
              <Button>Browse Competitions</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !entry) {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-error/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-display text-cream mb-2">Something went wrong</h1>
          <p className="text-cream/60 mb-6">
            {error || "We couldn't find your entry. Don't worry - if your payment was successful, your entry will appear in your account shortly."}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={`/competition/${raffleId}`}>
              <Button variant="secondary">Back to Competition</Button>
            </Link>
            <Link to="/browse">
              <Button>Browse Competitions</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-midnight">
      {/* Confetti celebration */}
      <ConfettiExplosion count={80} duration={4} />

      <div className="page-container py-12 md:py-20">
        <div className="max-w-2xl mx-auto text-center">
          {/* Success icon */}
          <div className="mb-6 animate-winner-reveal">
            <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto border-2 border-primary/40">
              <svg className="w-10 h-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>

          {/* Heading */}
          <h1 className="text-3xl md:text-4xl font-display text-cream mb-3">
            You're in!
          </h1>
          <p className="text-lg text-cream/70 mb-8">
            You've entered the competition for <span className="text-primary">{raffle?.title}</span>
          </p>

          {/* Ticket Numbers Card */}
          <div className="bg-midnight-light border border-primary/20 rounded-xl p-6 md:p-8 mb-8">
            <p className="text-xs font-bold uppercase tracking-wider text-cream/50 mb-4">
              Your Entry {ticketCount > 1 ? 'Numbers' : 'Number'}
            </p>

            {/* Ticket number display */}
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              {ticketNumbers.map((num, index) => (
                <div
                  key={num}
                  className="w-14 h-14 bg-midnight border border-primary/30 rounded-lg flex items-center justify-center shadow-glow-primary animate-number-slot"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <span className="font-mono text-xl font-bold text-primary">
                    {num}
                  </span>
                </div>
              ))}
            </div>

            {/* Entry details */}
            <div className="border-t border-midnight-lighter pt-4 mt-4">
              <div className="flex justify-between text-sm">
                <span className="text-cream/50">Entries purchased</span>
                <span className="text-cream font-medium">{ticketCount}</span>
              </div>
              <div className="flex justify-between text-sm mt-2">
                <span className="text-cream/50">Total paid</span>
                <span className="text-cream font-medium">
                  {formatCurrency(entry.totalPaid)}
                </span>
              </div>
            </div>
          </div>

          {/* Raffle info */}
          {raffle && (
            <div className="bg-midnight-light border border-midnight-lighter rounded-xl p-4 mb-8">
              <div className="flex items-center gap-4">
                {raffle.photos?.[0] && (
                  <img
                    src={raffle.photos[0]}
                    alt={raffle.title}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                )}
                <div className="flex-1 text-left">
                  <h3 className="font-semibold text-cream line-clamp-1">
                    {raffle.title}
                  </h3>
                  <p className="text-sm text-cream/50">
                    {raffle.ticketsSold}/{raffle.ticketQuantity} entries
                  </p>
                </div>
                <Link to={`/competition/${raffleId}`}>
                  <Button variant="ghost" size="sm">
                    View
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* What's next */}
          <div className="text-left bg-midnight-light border border-midnight-lighter rounded-xl p-6 mb-8">
            <h3 className="font-semibold text-cream mb-4">What happens next?</h3>
            <ul className="space-y-3 text-sm text-cream/70">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary text-xs font-bold">1</span>
                </span>
                <span>We'll notify you when the competition ends and the winner is drawn</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary text-xs font-bold">2</span>
                </span>
                <span>If you win, you'll receive an email and SMS with collection details</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary text-xs font-bold">3</span>
                </span>
                <span>Arrange pickup with the seller and confirm handover in the app</span>
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/browse">
              <Button size="lg" className="w-full sm:w-auto">
                Browse More Competitions
              </Button>
            </Link>
            <Link to={`/competition/${raffleId}`}>
              <Button variant="secondary" size="lg" className="w-full sm:w-auto">
                Back to Competition
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
